import { ApolloClient, MutationFunction } from '@apollo/client';
// Types
import { CBDeliverableItemType } from 'api/graphql-global-types';
import {
  CreateCampaignDeliverableFiles,
  CreateCampaignDeliverableFilesVariables,
  CreateCampaignDeliverableFiles_createCampaignDeliverableFiles,
} from 'api/campaignV2/types/CreateCampaignDeliverableFiles';
// Helpers
import { isImageType } from './campaign';
import {
  DeliverableFileUploadResult,
  isFileNeedingConversion,
} from './deliverableFileUpload';

type FileToUpload = {
  fields: string;
  url: string;
  name: string;
  type: string;
  file: File;
  key?: string;
};

export type SubmitInputItem = {
  deliverableItemId: string;
  type: CBDeliverableItemType;
  name: string;
  key: string;
  videoTranscodingJobId?: string | null;
};

export type UploadResult = {
  submitInput: SubmitInputItem[];
  conversionJobIds: string[];
};

const getVideoTranscodingJobId = (
  result: DeliverableFileUploadResult,
  originalFile: File
): string | undefined => {
  const needsConversion =
    result.needsConversion ?? isFileNeedingConversion(originalFile);

  if (!needsConversion) {
    return undefined;
  }

  if (!result.convertJobId) {
    throw new Error(
      `Converted upload ${originalFile.name} is missing convertJobId. Submission cannot proceed without videoTranscodingJobId.`
    );
  }

  return result.convertJobId;
};

export const processAllDeliverableFilesWithTranscoding = async (
  files: File[],
  deliverableItemId: string,
  campaignId: string,
  createCampaignDeliverableFiles: MutationFunction<
    CreateCampaignDeliverableFiles,
    CreateCampaignDeliverableFilesVariables
  >,
  uploadDeliverables: (
    preSignedUrls: CreateCampaignDeliverableFiles_createCampaignDeliverableFiles[],
    files?: File[]
  ) => Promise<FileToUpload[]>,
  uploadDeliverableFileWithConversion: (
    file: File,
    campaignId: string,
    deliverableItemId: string,
    client: ApolloClient<any>,
    onProgress?: (percentage: number) => void
  ) => Promise<DeliverableFileUploadResult>,
  apolloClient: ApolloClient<any>,
  onProgress?: (
    fileName: string,
    progress: number,
    stage: 'uploading' | 'converting'
  ) => void
): Promise<UploadResult> => {
  const imageFiles = files.filter((file) => isImageType(file));
  const videoFiles = files.filter((file) => !isImageType(file));

  const imageResults = await Promise.all(
    imageFiles.map(async (file) => {
      if (onProgress) {
        onProgress(file.name, 0, 'uploading');
      }

      const { data: presignedUrls } = await createCampaignDeliverableFiles({
        variables: {
          input: {
            files: [{ name: file.name }],
          },
        },
      });

      if (!presignedUrls?.createCampaignDeliverableFiles.length) {
        throw new Error('Failed to get presigned URL for image');
      }

      const uploadedUrls = await uploadDeliverables(
        presignedUrls.createCampaignDeliverableFiles,
        [file]
      );

      if (!uploadedUrls.length || !uploadedUrls[0].key) {
        throw new Error('Failed to upload image');
      }

      return {
        deliverableItemId,
        type: CBDeliverableItemType.FILE,
        name: file.name,
        key: uploadedUrls[0].key,
      } as SubmitInputItem;
    })
  );

  const videoResults = await Promise.all(
    videoFiles.map(async (file) => {
      if (onProgress) {
        onProgress(file.name, 0, 'uploading');
      }

      const result = await uploadDeliverableFileWithConversion(
        file,
        campaignId,
        deliverableItemId,
        apolloClient,
        (percentage: number) => {
          if (onProgress) {
            onProgress(file.name, percentage, 'uploading');
          }
        }
      );

      return {
        result,
        submitItem: {
          deliverableItemId,
          type: CBDeliverableItemType.FILE,
          name: result.name,
          key: result.key,
          videoTranscodingJobId: getVideoTranscodingJobId(result, file),
        } as SubmitInputItem,
      };
    })
  );

  const submitInput = [
    ...imageResults,
    ...videoResults.map((r) => r.submitItem),
  ];
  const conversionJobIds = videoResults
    .map((r, index) =>
      getVideoTranscodingJobId(r.result, videoFiles[index] as File)
    )
    .filter((jobId): jobId is string => Boolean(jobId));

  return { submitInput, conversionJobIds };
};

export const buildSubmitInputFromUploadResults = (
  uploadResults: UploadResult[],
  transcodingResults: Map<string, string>
): SubmitInputItem[] => {
  return uploadResults.flatMap((result) =>
    result.submitInput.map((item) => {
      if (item.videoTranscodingJobId) {
        const resultKey = transcodingResults.get(item.videoTranscodingJobId);
        if (!resultKey) {
          throw new Error(
            `Transcoding job ${item.videoTranscodingJobId} did not produce a resultKey for file ${item.name}. Submission cannot proceed without resultKey.`
          );
        }

        return {
          deliverableItemId: item.deliverableItemId,
          type: item.type,
          name: item.name,
          key: resultKey,
          videoTranscodingJobId: item.videoTranscodingJobId,
        };
      }

      return {
        deliverableItemId: item.deliverableItemId,
        type: item.type,
        name: item.name,
        key: item.key,
      };
    })
  );
};
